import React, { useState, useEffect } from "react";
import Moment from "moment";
import usePageTitle from 'hooks/usePageTitle';
import PageMenu from "components/blocks/PageMenu/PageMenu";
import useDecode from "../../../hooks/useDecode";
import useGetRole from "hooks/useGetRole";
import DOMPurify from "dompurify";
import useGetAllNoticesByUser from '../../../services/notices/useGetAllNoticesByUser';
import useUpdateNotice from "services/notices/useUpdateNotices";
import { useGeneralManagementState } from "../GeneralManagement/generalManagementState";
import Loader from "components/blocks/Loader/Loader";
import FilesNoticesGrid from "./FilesNoticesGrid";
import Icon from "components/blocks/Icon/Icon";
import NoData from "components/pages/noData/NoData";
import "components/blocks/Grids/Cards.scss";
import "components/blocks/CommsList/CommsList.scss";
import "./notices.scss";

const PAGE_SIZE = 5;

const NoticesGrid = () => {
  usePageTitle('Avisos');
  const [showModal, setShowModal]             = useState(false);
  const [selectedMessage, setSelectedMessage] = useState("");
  const [filesNotice, setFilesNotice]         = useState([]);
  const [viewedActiveTab, setViewedActiveTab] = useState(false);
  const [allViewedNotices, setAllViewedNotices]       = useState([]);
  const [allNotViewedNotices, setAllNotViewedNotices] = useState([]);
  const [idNoticeSelected, setIdNoticeSelected]     = useState("");
  const [selectedNoticeTitle, setSelectedNoticeTitle] = useState("");
  const [stateNotice, setStateNotice]               = useState(true);
  const [noticeUpdated, setNoticeUpdated]       = useState(0);
  const [page, setPage]                         = useState(1);

  const { getAllUserNotices, allUserNotices, loadingGetAllUserNotices } = useGetAllNoticesByUser();
  const { updateNotice }   = useUpdateNotice();
  const tokenResponse      = useDecode("token");
  const idUser             = tokenResponse?.extension_id;
  const { isImpersonation } = useGetRole;
  const { state, dispatch } = useGeneralManagementState();

  useEffect(() => { getAllUserNotices(idUser); }, []);

  useEffect(() => {
    if (allUserNotices.length > 0) {
      const viewed    = allUserNotices.filter((n) => n.leido);
      const notViewed = allUserNotices.filter((n) => !n.leido);
      setAllViewedNotices(viewed);
      setAllNotViewedNotices(notViewed);
      if (notViewed.length < 1 && viewed.length >= 1) setViewedActiveTab(true);
    }
  }, [allUserNotices]);

  const handleSelectTab = (viewed) => {
    setViewedActiveTab(viewed);
    setPage(1);
  };

  const handleCardClick = (notice) => {
    setIdNoticeSelected(notice.id);
    setSelectedNoticeTitle(notice.titulo);
    setSelectedMessage(notice.html);
    setFilesNotice(notice.adjuntos);
    setStateNotice(notice.leido);
    setShowModal(true);
  };

  const handleClose = () => {
    const impersonation = isImpersonation();
    if (impersonation) { setShowModal(false); return; }

    if (!stateNotice) {
      updateNotice(idUser, idNoticeSelected);
      setNoticeUpdated((p) => p + 1);
      setAllNotViewedNotices((prev) => prev.filter((n) => n.id !== idNoticeSelected));
      setAllViewedNotices((prev) => [
        ...prev,
        ...allNotViewedNotices.filter((n) => n.id === idNoticeSelected),
      ]);
      dispatch({ type: "REFRESH_COUNT_NOTICE", payload: true });
    }

    setTimeout(() => {
      dispatch({ type: "REFRESH_COUNT_NOTICE", payload: false });
      setShowModal(false);
    }, 500);
  };

  const notices = viewedActiveTab ? allViewedNotices : allNotViewedNotices;
  const totalPages = Math.max(1, Math.ceil(notices.length / PAGE_SIZE));
  const safePage   = Math.min(page, totalPages);
  const paged      = notices.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  return (
    <>
      <PageMenu title="Avisos" subtitle="Avisos, actualizaciones e información especial" />

      <div className="container">
        {/* ── Tabs ── */}
        <div className="notices-tabs">
          <button
            type="button"
            className={`notices-tab${!viewedActiveTab ? " notices-tab--active" : ""}`}
            onClick={() => handleSelectTab(false)}
          >
            No leídos
            {allNotViewedNotices?.length > 0 && (
              <span className="notices-tab__count">{allNotViewedNotices.length}</span>
            )}
          </button>
          <button
            type="button"
            className={`notices-tab${viewedActiveTab ? " notices-tab--active" : ""}`}
            onClick={() => handleSelectTab(true)}
          >
            Leídos
            {allViewedNotices?.length > 0 && (
              <span className="notices-tab__count notices-tab__count--muted">
                {allViewedNotices.length}
              </span>
            )}
          </button>
        </div>

        {/* ── Loader ── */}
        {loadingGetAllUserNotices && (
          <Loader activeLoader label="Descargando Avisos" />
        )}

        {/* ── Card list ── */}
        {!loadingGetAllUserNotices && (
          <>
            {notices.length === 0 ? (
              <NoData message={viewedActiveTab ? "No hay avisos leídos." : "No hay avisos pendientes."} />
            ) : (
              <div className="cards-list" style={{ marginTop: 16 }}>
                {paged.map((notice) => (
                  <article
                    key={notice.id}
                    className="item-card item-card--compact item-card--4col item-card--clickable"
                    role="button"
                    tabIndex={0}
                    onClick={() => handleCardClick(notice)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleCardClick(notice);
                      }
                    }}
                  >
                    <div className={`item-card__icon${!notice.leido ? " item-card__icon--amber" : ""}`}>
                      <Icon name="bell" size={18} />
                    </div>

                    <div className="item-card__content">
                      <div className="item-card__title">{notice.titulo}</div>
                      {!notice.leido && (
                        <div className="item-card__meta">
                          <span className="status-badge status-badge--warn">Nuevo</span>
                        </div>
                      )}
                    </div>

                    <div className="item-card__badge">
                      <span className="item-card__sub" style={{ whiteSpace: "nowrap" }}>
                        {Moment(notice.fechaHora).isValid()
                          ? Moment(notice.fechaHora).format("DD MMM YYYY")
                          : ""}
                      </span>
                    </div>

                    <div className="item-card__action">
                      <Icon name="arrow-right" size={16} />
                    </div>
                  </article>
                ))}
              </div>
            )}

            {/* ── Pager ── */}
            <div
              className="comms-list__pager"
              style={{ visibility: totalPages > 1 ? "visible" : "hidden" }}
            >
              <span className="comms-list__pager-info">
                {(safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, notices.length)} de {notices.length}
              </span>
              <div className="comms-list__pager-btns">
                <button
                  className="btn btn--ghost btn--sm"
                  disabled={safePage <= 1}
                  onClick={() => setPage(safePage - 1)}
                >
                  <Icon name="arrow-left" size={16} /> Anterior
                </button>
                <button
                  className="btn btn--ghost btn--sm"
                  disabled={safePage >= totalPages}
                  onClick={() => setPage(safePage + 1)}
                >
                  Siguiente <Icon name="arrow-right" size={16} />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* ── Modal detalle ── */}
      {showModal && (
        <>
          <div className="notice-modal-backdrop" onClick={handleClose} />
          <div className="notice-modal" role="dialog" aria-modal="true">
            <div className="notice-modal__header">
              <h2 className="notice-modal__title">{selectedNoticeTitle}</h2>
              <button className="notice-modal__close" onClick={handleClose} aria-label="Cerrar">
                ×
              </button>
            </div>
            <div className="notice-modal__body">
              <div
                className="notice-modal__content"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selectedMessage) }}
              />
              {filesNotice?.length > 0 && (
                <div className="notice-modal__files">
                  <h4 className="title-files-notices">Archivos adjuntos</h4>
                  <FilesNoticesGrid documents={filesNotice} />
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default NoticesGrid;
